// Zones management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Zone } from '@/types/entities'
import { isAssistantLoggedIn } from '@/lib/permissions'

export const zoneKeys = {
  all: ['zones'] as const,
  list: (restaurantId: string) => [...zoneKeys.all, 'list', restaurantId] as const,
}

/**
 * Fetch all zones for a restaurant
 */
export function useZones(restaurantId: string | null) {
  return useQuery({
    queryKey: zoneKeys.list(restaurantId || ''),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    queryFn: async (): Promise<Zone[]> => {
      if (!restaurantId) {
        return []
      }

      // Assistants use server-side permission-checked RPC
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_get_zones', {
          
          p_restaurant_id: restaurantId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            return []
          }
          throw error
        }

        return (data || []).map((z: any) => ({
          id: z.id,
          restaurantId: z.restaurant_id,
          nameAr: z.name_ar,
          nameFr: z.name_fr,
          isActive: z.is_active,
          sortOrder: z.sort_order,
          createdAt: z.created_at,
          updatedAt: z.updated_at,
        }))
      }

      const { data, error } = await supabase.rpc('get_zones', { p_restaurant_id: restaurantId })

      if (error) {
        throw error
      }

      return (data || []).map((z: any) => ({
        id: z.id,
        restaurantId: z.restaurant_id,
        nameAr: z.name_ar,
        nameFr: z.name_fr,
        isActive: z.is_active,
        sortOrder: z.sort_order,
        createdAt: z.created_at,
        updatedAt: z.updated_at,
      }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Create a new zone
 */
export function useCreateZone(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      nameAr: string
      nameFr: string
    }) => {
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_create_zone', {
          
          p_restaurant_id: restaurantId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) throw new Error('Permission denied')
          throw error
        }

        return data
      }

      const { data, error } = await supabase.rpc('create_zone', {
        p_restaurant_id: restaurantId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: zoneKeys.list(restaurantId),
      })
    },
  })
}

/**
 * Update an existing zone
 */
export function useUpdateZone(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      zoneId: string
      nameAr: string
      nameFr: string
      isActive: boolean
    }) => {
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_update_zone', {
          
          p_zone_id: request.zoneId,
          p_restaurant_id: restaurantId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
          p_description_ar: null,
          p_description_fr: null,
          p_is_active: request.isActive,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) throw new Error('Permission denied')
          throw error
        }

        return data
      }

      const { data, error } = await supabase.rpc('update_zone', {
        p_zone_id: request.zoneId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_is_active: request.isActive,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: zoneKeys.list(restaurantId),
      })
    },
  })
}

/**
 * Delete a zone
 */
export function useDeleteZone(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (zoneId: string) => {
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_delete_zone', {
          
          p_zone_id: zoneId,
          p_restaurant_id: restaurantId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) throw new Error('Permission denied')
          throw error
        }

        return data
      }

      const { data, error } = await supabase.rpc('delete_zone', { p_zone_id: zoneId })

      if (error) {
        // Check if it's a foreign key constraint error
        if (error.code === '23503') {
          throw new Error('ZONE_IN_USE')
        }
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: zoneKeys.list(restaurantId),
      })
    },
  })
}








